import { Component } from 'react'
import * as styles from 'components/features/pfm/PostScope/PostScope.styl'
import * as cn from 'classnames'
import { peersRelationSelector } from 'selectors/peergroupSelectors'
import { connectOptionally, OptionalRendererProps, NO_RENDER, createPositiveRendererProps } from 'tangle-shared'
import { filterRelationsSelector } from 'selectors/feedSelectors'
import { PeergroupRelation } from 'interfaces/peergroups'
import { SelectableFeedFilter, FeedgroupSlug, ComposeId } from 'interfaces/feed'
import { ActionMenu } from 'components/features/pfm/ActionMenu/ActionMenu'
import { ActionMenuItem } from 'components/features/pfm/ActionMenu/ActionMenuItem'
import { MapDispatchToPropsFunction } from 'react-redux'
import { feedComposeSelectFilterActionCreator } from 'actions/feedComposeActions'
import { createSelector } from 'reselect'
import { newPostComposeSelector } from 'selectors/feedComposeSelectors'

type PostScopeOwnState = {
  isExpanded: boolean
  scopeTargetProps: SelectableFeedFilter | null
  scopeTargetState: SelectableFeedFilter | null
}

type PostScopeStateProps = {
  yourRelation: PeergroupRelation
  otherRelations: PeergroupRelation[]
  scopeTarget: SelectableFeedFilter | null
}

type PostScopeDispatchProps = {
  onSelect: (filter: SelectableFeedFilter) => void
}

type PostScopeOwnProps = {
  composeId: ComposeId
}

type PostScopeInnerProps =
  & PostScopeStateProps
  & PostScopeDispatchProps
  & PostScopeOwnProps

  type PostScopeProps =
  & PostScopeInnerProps

class PostScopeComponent extends Component<PostScopeProps, PostScopeOwnState> {

  static getDerivedStateFromProps (nextProps: PostScopeProps, prevState: PostScopeOwnState): Partial<PostScopeOwnState> | null {
    let nextState: Partial<PostScopeOwnState> = {
      scopeTargetProps: nextProps.scopeTarget,
    }

    const isPropsChanged = nextProps.scopeTarget !== prevState.scopeTargetProps
    if (isPropsChanged) {
      nextState = {
        ...nextState,
        scopeTargetState: null,
      }
    }
    return nextState
  }

  state = {
    isExpanded: false,
    scopeTargetProps: this.props.scopeTarget,
    scopeTargetState: null,
  }

  render () {
    const { yourRelation } = this.props
    const scopeTarget = this.getSelectedTarget()

    const selectorClassName = cn(
      styles.scopeSelector,
      {
        [styles.yourPeers]: scopeTarget && (
          (scopeTarget.type === 'peergroup' && scopeTarget.relationId === yourRelation.id)
          || scopeTarget.type === 'your-posts'
        ),
      },
    )

    return (
      <div className={ styles.container }>
        <div className={ selectorClassName } onClick={ this.handleClick }>
          Post for
          <div className={ styles.scopeTitle }>{ this.renderSelectedTitle() }</div>
          <div className={ styles.arrow } />
        </div>
        { this.renderActions() }
      </div>
    )
  }

  componentDidMount () {
    const scopeTarget = this.getSelectedTarget()

    if (!scopeTarget) {
      this.handleSelectFeedgroup('everyone')
    }
  }

  private renderSelectedTitle () {
    const {
      yourRelation,
      otherRelations,
    } = this.props

    const scopeTarget = this.getSelectedTarget()

    if (!scopeTarget) {
      return 'Select Peers'
    }

    switch (scopeTarget.type) {
      case 'peergroup':

        if (scopeTarget.relationId === yourRelation.id) {
          return 'Your Peers'
        }

        const selectedRelation = otherRelations.find((relation) => relation.id === scopeTarget.relationId)

        return selectedRelation && selectedRelation.name

      case 'feedgroup':
        if (scopeTarget.slug === 'everyone') {
          return 'Everyone'
        }

        return scopeTarget.slug

      default:
        return 'Select a Group'
    }
  }

  private renderActions () {
    const {
      yourRelation,
      otherRelations,
    } = this.props

    const {
      isExpanded,
    } = this.state

    const scopeTarget = this.getSelectedTarget()

    const actionsClassName = cn(
      styles.actions,
      {
        [styles.shown]: isExpanded,
      }
    )

    return (
      <div className={ actionsClassName } >
        <ActionMenu onHide={ this.hideActionsMenu }>

          <ActionMenuItem
            isActive={
              !!scopeTarget && (
                (scopeTarget.type === 'peergroup' && scopeTarget.relationId === yourRelation.id)
                || scopeTarget.type === 'your-posts'
              )
            }
            onClick={ () => this.handleSelectPeergroup(yourRelation) }
          >
            { yourRelation.name }
          </ActionMenuItem>

          <ActionMenuItem
            isActive={ !!scopeTarget && scopeTarget.type === 'feedgroup' }
            onClick={ () => this.handleSelectFeedgroup('everyone') }
          >
            Everyone
          </ActionMenuItem>

          {
            otherRelations.map(
              (relation) => relation.slug !== 'random' && (
                <ActionMenuItem
                  key={ relation.id }
                  isActive={
                    !!scopeTarget
                  && scopeTarget.type === 'peergroup'
                  && scopeTarget.relationId === relation.id
                  }
                  onClick={ () => this.handleSelectPeergroup(relation) }
                >
                  { relation.name }
                </ActionMenuItem>
              )
            )
          }
        </ActionMenu>
      </div>
    )
  }

  private getSelectedTarget () {
    const {
      scopeTargetState,
      scopeTargetProps,
    } = this.state

    return scopeTargetState || scopeTargetProps
  }

  private hideActionsMenu = () => {
    this.setState({
      isExpanded: false,
    })
  }

  private setTarget (scopeTarget: SelectableFeedFilter) {
    const { onSelect } = this.props
    this.setState({
      scopeTargetState: scopeTarget,
      isExpanded: false,
    })
    onSelect(scopeTarget)
  }

  private handleClick = () => {
    this.setState({
      isExpanded: true,
    })
  }

  private handleSelectPeergroup = (relation: PeergroupRelation) => {
    this.hideActionsMenu()
    this.setTarget({
      type: 'peergroup',
      relationId: relation.id,
    })
  }

  private handleSelectFeedgroup = (slug: FeedgroupSlug) => {
    this.setTarget({
      type: 'feedgroup',
      slug,
    })
  }
}

const mapStateToProps = createSelector(
  peersRelationSelector,
  filterRelationsSelector,
  newPostComposeSelector,
  (yourRelation, otherRelations, composeState): OptionalRendererProps<PostScopeStateProps> => {
    if (!yourRelation || !composeState) {
      return NO_RENDER()
    }

    return createPositiveRendererProps({
      yourRelation,
      otherRelations,
      scopeTarget: composeState.filter || null,
    })
  }
)


const mapDispatchToProps: MapDispatchToPropsFunction<PostScopeDispatchProps, PostScopeOwnProps> =
  (dispatch, { composeId }) => ({
    onSelect: (filter) => dispatch(feedComposeSelectFilterActionCreator({ composeId, filter })),
  })

export const PostScope = connectOptionally<
  PostScopeStateProps,
  PostScopeDispatchProps,
  PostScopeOwnProps
>(mapStateToProps, mapDispatchToProps)(PostScopeComponent)
